import React, {useState, useEffect, useMemo} from "react";
import dayjs from "dayjs";
import {useOutletContext} from "react-router-dom";
import {useCurrentUser} from "../../utils/CurrentUserContext";
import {useCubeQuery} from "../../utils/hooks/useCubeQuery";
import Table from "../../components/lists/Table/Table";
import {nestWebsiteTDTime} from "../../utils/helperfuctions";
import {Button, Select, Tooltip} from "antd";
import DateSelect from "../../components/buttons/DateSelect";
import {useDateRange} from "../../utils/date-utils";
import {CompanyType, TeaserStatus} from "../../__generated__/graphql";
import TopStatsTime from "./ReportingComponents/TopStatsTime";
import ActionButton from "../../components/lists/Table/shared/ActionButton";
import {PauseCircleIcon, PlayCircleIcon, PencilIcon, PlusCircleIcon} from "@heroicons/react/24/outline";
import TrafficDriverModal from "../../components/modals/TrafficDriverModal";
import {useMutation, useQuery} from "@apollo/client";
import {CREATE_TRAFFIC_DRIVER, GET_TRAFFIC_DRIVERS, UPDATE_TRAFFIC_DRIVER} from "../../utils/api/trafficDriver";
import Cookies from 'js-cookie';
import Spinner from "../../components/icons/Spinner";
import useCubeClient from "../../utils/cubejs-client";
import TimeGraph from "./ReportingComponents/TimeGraph";
import {exportToExcelRevenue} from "../Settings/SettingsComponents/BillingComponents/costUtils";
import BetaFeatureToast from "../../components/BetaFeatureToast";
import {withAsyncErrorToast} from "../../utils/withToast";
import {App as AntdApp} from "antd";

const convertedTD = (trafficDrivers) => {
  const tdOutput = trafficDrivers.reduce((acc, driver) => {
    acc[driver._id] = driver.status;
    return acc;
  }, {});
  return tdOutput;
}

const ReportNewTime = () => {
  const {notification} = AntdApp.useApp();
  const {user} = useCurrentUser();
  const {campaign} = useOutletContext();
  const cubejsApi = useCubeClient()
  const [showModal, setShowModal] = useState(false);
  const [selectedTeaser, setSelectedTeaser] = useState(false);
  const tempCampStartData = dayjs(campaign.startDate)
  const campStartData = tempCampStartData.subtract(1, 'day');

  // Check if user is a publisher who doesn't own the campaign
  const isPublisherNotOwner = user.company.type === CompanyType.Publisher && campaign.ownerCompanyId !== user.companyId;

  const {data, loading} = useQuery(GET_TRAFFIC_DRIVERS, {
    variables: {
      perPage: 1000,
      filters: {
        campaignId: campaign._id,
      }
    }
  })

  // Filter teasers client-side if publisher doesn't own campaign
  let teasers = data ? data.trafficDrivers.items : [];

  // TODO: Remove after this filter is done on the BE
  if (isPublisherNotOwner) {
    teasers = teasers
      .map(teaser => ({...teaser, websites: teaser.websites.filter(website => website.publisherId === user.companyId)}))
      .filter(teaser => teaser.websites.length > 0);
  }

  const [createTrafficDriver] = useMutation(CREATE_TRAFFIC_DRIVER, {
    refetchQueries: ["TeaserMany"],
    awaitRefetchQueries: true,
  });
  const [updateTrafficDriver] = useMutation(UPDATE_TRAFFIC_DRIVER, {
    refetchQueries: ["TeaserMany"],
    awaitRefetchQueries: true,
  });

  // TODO: remove tdObject and take status directly from row
  const tdObject = convertedTD(teasers)

  const getAllColumns = (onCreateOrUpdate, onUpdateStatus) => {
    const columns = [
    {
      title: 'Teaser',
      dataIndex: "name",
      key: "name",
      fixed: "left",
      render: (text, record) => {
        if (!text) return '-';
        const isTruncated = text.length > 30;
        return (
          <Tooltip title={isTruncated ? text : null}>
            {isTruncated ? `${text.substring(0, 30)}...` : text} {record.removed ? <strong>(removed)</strong> : ''}
          </Tooltip>
        );
      },
    },
    {
      title: "Thumbnail",
      dataIndex: "tdImage",
      key: "tdImage",
      render: (tdImage) => (
        tdImage && tdImage !== "N/A"
          ? <img
            src={`https://res.cloudinary.com/dzhcms4s6/image/upload/c_thumb,h_50/f_png/${tdImage}`}
            alt="Thumbnail"
          />
          : '-'
      ),
    },
    {
      title: "Impressions",
      dataIndex: "tdImpressions",
      key: "impressions",
      sorter: (a, b) => a.tdImpressions - b.tdImpressions,
      render: (text) => text !== null && text !== undefined
        ? parseInt(text).toLocaleString() // Add thousand separators
        : '-',
    },
    {
      title: "Clicks",
      dataIndex: "tdClicks",
      key: "clicks",
      sorter: (a, b) => a.tdClicks - b.tdClicks,
      render: (text) => text !== null && text !== undefined ? text : '-',
    },
    {
      title: "Reads",
      dataIndex: "reads",
      key: "reads",
      sorter: (a, b) => a.reads - b.reads,
      render: (text) => text !== null && text !== undefined ? text : '-',
    },
    {
      title: "Revenue",
      dataIndex: "publisherRevenue",
      key: "revenue",
      sorter: (a, b) => a.publisherRevenue - b.publisherRevenue,
      render: (text) => text !== null && text !== undefined
        ? parseFloat(text).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) // Add thousand separators and format to 2 decimal places
        : '-',
    },
    {
      title: "Total Cost",
      dataIndex: "totalCost",
      key: "totalCost",
      sorter: (a, b) => a.totalCost - b.totalCost,
      render: (text) => text !== null && text !== undefined
        ? parseFloat(text).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) // Add thousand separators and format to 2 decimal places
        : '-',
    },
    {
      title: "Clickouts",
      dataIndex: "clickOuts",
      key: "click_outs",
      sorter: (a, b) => a.clickOuts - b.clickOuts,
      render: (text) => text !== null && text !== undefined ? text : '-',
    },
    {
      title: "Read time",
      dataIndex: "avgReadtime",
      key: "read_time",
      sorter: (a, b) => a.avgReadtime - b.avgReadtime,
      render: (text) => text !== null && text !== undefined ? text : '-',
    },
    {
      title: "Conversions",
      dataIndex: "conversions",
      key: "conversions",
      sorter: (a, b) => a.conversions - b.conversions,
      render: (text) => text !== null && text !== undefined ? text : '-',
    },
    {
      title: "eCPM",
      dataIndex: "eCpm",
      key: "eCPM",
      sorter: (a, b) => a.eCpm - b.eCpm,
      render: (text) => text !== null && text !== undefined ? text : '-',
    },
    {
      title: "CTR",
      dataIndex: "ctr",
      key: "ctr",
      sorter: (a, b) => a.ctr - b.ctr,
      render: (text) => text !== null && text !== undefined ? `${text}%` : '-',
    },
    {
      title: "Reads to impressions",
      dataIndex: "imps_reads",
      key: "imps_reads",
      sorter: (a, b) => a.imps_reads - b.imps_reads,
      render: (text) => text !== null && text !== undefined ? `${text}‰` : '-',
    },
    {
      title: "Click Out Rate",
      dataIndex: "cotr",
      key: "cotr",
      sorter: (a, b) => a.cotr - b.cotr,
      render: (text) => text !== null && text !== undefined ? `${text}%` : '-',
    },
    {
      key: "actions",
      fixed: "right",
      title: <ActionButton
        tooltip='Add teaser'
        onClick={onCreateOrUpdate}
        Icon={PlusCircleIcon}
      />,
      render: (_, row) => {
        if (row.trafficDriverId && !row.removed) {
          const isActive = tdObject[row.trafficDriverId] === TeaserStatus.Active
          return <>
            <ActionButton
              tooltip="Edit teaser"
              row={row}
              onClick={onCreateOrUpdate}
              Icon={PencilIcon}
            />
            <ActionButton
              tooltip={isActive ? "Pause teaser" : "Resume teaser"}
              row={row}
              onClick={(row) => onUpdateStatus(row, isActive ? TeaserStatus.Inactive : TeaserStatus.Active)}
              Icon={isActive ? PauseCircleIcon : PlayCircleIcon}
              className={isActive ? undefined : "text-green-800"}
            />
          </>
        }
      }
    },
    ];
    
    // Add CPA column only for non-publishers
    if (user.company.type !== CompanyType.Publisher) {
      columns.push({
        title: "CPA",
        dataIndex: "cpa",
        key: "cpa",
        sorter: (a, b) => a.cpa - b.cpa,
        render: (text) => text !== null && text !== undefined ? text : '-',
      });
    }
    
    return columns;
  };

  // Using the enhanced useDateRange hook with 300ms debounce
  const {
    dateRange,
    handleDateChange,
    formattedDateRange
  } = useDateRange(
    [
      campStartData.startOf('day'),
      dayjs().endOf('day')
    ],
    300 // 300ms debounce
  );

  // Custom handler that ensures day boundaries
  const handleDate = (newvalue) => {
    handleDateChange([
      newvalue[0].startOf('day'),
      newvalue[1].endOf('day'),
    ]);
  };
  const [visibleColumns, setVisibleColumns] = useState(['name', 'tdImpressions', 'tdClicks', 'reads']);

  const aggPublisherFilters = user.company.type === CompanyType.Publisher && campaign.ownerCompanyId !== user.companyId
    ? [{
      member: "aggregateTdLevel.publisherId",
      operator: "equals",
      values: [user.companyId],
    }]
    : [];


  const {
    resultSet: aggData,
    error: aggError,
    isLoading: aggIsLoading,
    refetch
  } = useCubeQuery(
    {
      dimensions: [
        "aggregateTdLevel.websiteId",
        "aggregateTdLevel.publicationId",
        "aggregateTdLevel.publisherId",
        "aggregateTdLevel.websiteName",
        "aggregateTdLevel.campaignBudget",
        "aggregateTdLevel.trafficDriverId",
        "aggregateTdLevel.tdHeadline",
        "aggregateTdLevel.tdImage",
        "aggregateTdLevel.campaignType",
        "aggregateTdLevel.tdType",
        "aggregateTdLevel.utmSource",
      ],
      measures: [
        "aggregateTdLevel.tdImpressions",
        "aggregateTdLevel.tdClicks",
        "aggregateTdLevel.reads",
        "aggregateTdLevel.totalCost",
        "aggregateTdLevel.publisherRevenue",
        "aggregateTdLevel.total_readtime",
        "aggregateTdLevel.clickouts",
        "aggregateTdLevel.conversions",
      ],
      order: {
        "aggregateTdLevel.websiteId": "asc",
      },
      filters: [
        {
          member: "aggregateTdLevel.campaignId",
          operator: "equals",
          values: [campaign._id],
        },
        {
          member: "aggregateTdLevel.websiteId",
          operator: "set",
        },
        {
          member: "aggregateTdLevel.publicationId",
          operator: "set",
        },
        {
          member: "aggregateTdLevel.fraud",
          operator: "notEquals",
          values: ["true"],
        },
        ...aggPublisherFilters,
      ],
      timeDimensions: [
        {
          dimension: "aggregateTdLevel.date",
          dateRange: formattedDateRange,
        },
      ],
    },
    {cubejsApi}
  );

  // Usage
  const result = aggData ? nestWebsiteTDTime(teasers, aggData.loadResponses[0].data) : {teaserData: [], totals: {}};
  const {teaserData, totals} = result;


  const saveColumnsToCookie = (columns) => {
    Cookies.set('selectedColumns', JSON.stringify(columns), {expires: 7});
  };

  const loadColumnsFromCookie = () => {
    const cookieColumns = Cookies.get('selectedColumns');
    return cookieColumns ? JSON.parse(cookieColumns) : ['name', 'tdImpressions', 'tdClicks', 'reads', 'tdImage'];
  };

  useEffect(() => {
    const initialColumns = loadColumnsFromCookie();
    setVisibleColumns(initialColumns);
  }, []);


  useEffect(() => {
    saveColumnsToCookie(visibleColumns);
  }, [visibleColumns]);

  const handleCreateTrafficDriver = async (record) => {
    const key = selectedTeaser ? 'Update teaser' : 'Create teaser'
    try {
      await withAsyncErrorToast(key, async () => {
        if (selectedTeaser) {
          await updateTrafficDriver({variables: {id: selectedTeaser.trafficDriverId, record}});
        } else {
          await createTrafficDriver({variables: {record}});
        }
        await refetch();
      }, undefined, notification)
    } catch (error) {
      // toast already shown in withAsyncErrorToast; swallow to avoid unhandled rejection
    }
  };

  const onUpdateStatus = (row, status) => withAsyncErrorToast('Update teaser status', async () => {
    // TODO: Update BE to allow updating just status without websiteIds
    await updateTrafficDriver({
      variables: {id: row.trafficDriverId, record: {status, websiteIds: row.children.map(child => child.websiteId)}}
    });
    await refetch();
  }, undefined, notification);

  const onCreateOrUpdate = (row) => {
    setSelectedTeaser(row)
    setShowModal(true)
  }

  const columns = getAllColumns(onCreateOrUpdate, onUpdateStatus)
    .filter(({ key, dataIndex }) => visibleColumns.includes(dataIndex) || key === 'actions');

  const options = useMemo(() => {
    const baseOptions = [
      {label: "Teaser", value: "name", disabled: true},
      {label: "Impressions", value: "tdImpressions", disabled: true},
      {label: "Clicks", value: "tdClicks", disabled: true},
      {label: "Reads", value: "reads", disabled: true},
      {label: "Thumbnail", value: "tdImage"},
      {label: "Type", value: "tdType"},
      {label: "Clickouts", value: "clickOuts"},
      {label: "Conversions", value: "conversions"},
      {label: "CTR", value: "ctr"},
      {label: "Reads to impressions", value: "imps_reads"},
      {label: "avgReadTime", value: "avgReadtime"},
      {label: "Click Out Rate", value: "cotr"},
    ];

    if (user.company.type === CompanyType.Publisher) {
      baseOptions.push({label: "Revenue", value: "publisherRevenue"});
      baseOptions.push({label: "eCPM", value: "eCpm"});
    } else {
      baseOptions.push({label: "CPA", value: "cpa"});
    }

    if (
      campaign.ownerCompanyId === user.companyId ||
      user.company.type === CompanyType.Agency ||
      user.company.type === CompanyType.Advertiser
    ) {
      baseOptions.push({label: "Total Cost", value: "totalCost"});
    }

    return baseOptions;
  }, [user.company.type, user.companyId, campaign.ownerCompanyId]);

  const getSummaryData = () => {
    const summary = {
      name: 'Total'
    };

    const totalImpressions = teaserData.reduce((sum, row) => sum + (row.tdImpressions || 0), 0);
    const totalRevenue = teaserData.reduce((sum, row) => sum + (row.publisherRevenue || 0), 0);
    const totalReads = teaserData.reduce((sum, row) => sum + (row.reads || 0), 0);
    const totalClickOuts = teaserData.reduce((sum, row) => sum + (row.clickOuts || 0), 0);
    const totalClicks = teaserData.reduce((sum, row) => sum + (row.tdClicks || 0), 0);
    const totalCost = teaserData.reduce((sum, row) => sum + (row.totalCost || 0), 0);

    if (visibleColumns.includes('tdImpressions')) {
      summary.tdImpressions = totalImpressions.toLocaleString();
    }
    if (visibleColumns.includes('tdClicks')) {
      summary.tdClicks = totalClicks;
    }
    if (visibleColumns.includes('reads')) {
      summary.reads = totalReads;
    }
    if (visibleColumns.includes('clickOuts')) {
      summary.clickOuts = totalClickOuts;
    }
    if (visibleColumns.includes('conversions')) {
      summary.conversions = teaserData.reduce((sum, row) => sum + (row.conversions || 0), 0);
    }
    if (visibleColumns.includes('publisherRevenue')) {
      summary.publisherRevenue = totalRevenue.toFixed(2);
    }
    if (visibleColumns.includes('eCpm') && totalImpressions > 0) {
      summary.eCpm = ((totalRevenue / totalImpressions) * 1000).toFixed(2);
    }
    if (visibleColumns.includes('totalCost')) {
      summary.totalCost = totalCost.toFixed(2);
    }
    if (visibleColumns.includes('ctr') && totalImpressions > 0) {
      summary.ctr = `${((totalClicks / totalImpressions) * 100).toFixed(2)}%`;
    }
    if (visibleColumns.includes('imps_reads') && totalImpressions > 0 && totalReads > 0) {
      summary.imps_reads = `${((totalReads*1000)/totalImpressions).toFixed(2)}‰`;
    }
    // Only calculate CPA for non-publisher companies
    if (visibleColumns.includes('cpa') && user.company.type !== CompanyType.Publisher) {
      const totalConversions = teaserData.reduce((sum, row) => sum + (row.conversions || 0), 0);
      if (totalConversions > 0) {
        summary.cpa = (totalCost / totalConversions).toFixed(2);
      }
    }
    if (visibleColumns.includes('cotr') && totalReads > 0) {
      summary.cotr = `${((totalClickOuts / totalReads) * 100).toFixed(2)}%`;
    }

    return summary;
  };

  if (aggIsLoading || loading) {
    return <Spinner/>;
  }
  console.log(teaserData)
  return (
    <div className="mb-4">
      <BetaFeatureToast
        title="New Beta Reporting Available!"
        description="Try our new improved reporting interface."
        buttonText="Try Beta Reporting"
        targetRoute="../report-prototype"
      />
      <TopStatsTime totalData={totals}/>
      <div className="flex mt-4 justify-between w-full items-center">

        <DateSelect dateRange={dateRange} handleDate={handleDate}/>

        <Select
          className="mb-2"
          mode="multiple"
          options={options}
          value={visibleColumns}
          onChange={setVisibleColumns}
        />
      </div>
      <Table
        columns={columns}
        data={teaserData}
        searchable={false}
        showSummary
        summaryData={getSummaryData()}
        footer={() => (
          <Button onClick={() => {
            // Create a copy of the data to avoid modifying the original
            const exportData = teaserData.map(teaser => {
              const teaserCopy = {...teaser};
              
              // Remove the specified fields
              delete teaserCopy.key;
              delete teaserCopy.trafficDriverId;
              delete teaserCopy.publicationId;
              delete teaserCopy.type;
              delete teaserCopy.eCpm;
              delete teaserCopy.tdImage;
              delete teaserCopy.removed;

              
              // Remove fields based on user company type
              if (user.company.type === CompanyType.Agency || user.company.type === CompanyType.Advertiser) {
                delete teaserCopy.publisherRevenue;
              }
              
              if (user.company.type === CompanyType.Publisher) {
                delete teaserCopy.totalCost;
              }
              
              // Also filter children if they exist
              if (teaserCopy.children) {
                teaserCopy.children = teaserCopy.children.map(child => {
                  const childCopy = {...child};
                  
                  // Remove the same fields from children
                  delete childCopy.key;
                  delete childCopy.websiteId;
                  delete childCopy.publicationId;
                  delete childCopy.type;
                  delete childCopy.eCpm;
                  delete childCopy.removed;
                  
                  if (user.company.type === CompanyType.Agency || user.company.type === CompanyType.Advertiser) {
                    delete childCopy.publisherRevenue;
                  }
                  
                  if (user.company.type === CompanyType.Publisher) {
                    delete childCopy.totalCost;
                  }
                  
                  return childCopy;
                });
              }
              
              return teaserCopy;
            });

            exportToExcelRevenue(exportData, "WebsitesData");
          }}>
            Export to Excel
          </Button>
          )
        }
      />
      <TrafficDriverModal
        open={showModal}
        editMode={!!selectedTeaser}
        defaultValues={selectedTeaser ? {
          headline: selectedTeaser.name,
          websiteIds: selectedTeaser.children.map(child => child.websiteId),
          publicationId: selectedTeaser.publicationId,
          imageId: selectedTeaser.tdImage,
        } : {
          campaignId: campaign._id,
        }}
        websites={campaign.websites}
        onCancel={() => {
          setSelectedTeaser(null)
          setShowModal(false)
        }}
        onOk={handleCreateTrafficDriver}
      />
      <TimeGraph aggPublisherFilters={aggPublisherFilters} dateRange={formattedDateRange} campaignId={campaign._id}
                 cubejsApi={cubejsApi}/>
    </div>
  );
};

export default ReportNewTime;
